import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useUpsertUserReactionMutation } from "src/api/client/user-reactions.api";
import { getReactionsCount } from "src/components/common/aggregated-reactions-bar/utils/get-reactions-count";
import { useNewsUserAggregatedReactions } from "src/components/pages/news/hooks/use-news-user-aggregated-reactions";
import { IUserReactionWithBarConfig, TUserReactions } from "src/components/user-reactions/types";
import { getHighestReactionNumber, getReactionsWithBarConfig } from "src/components/user-reactions/utils";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";
import { EnumReactionEntityType, EnumReactionType } from "src/types/common-enum";
import { getObjectCopy } from "src/utils/common-util";

export const useNewsUserReactions = (reactions: TUserReactions) => {
    const newsArticleId = useRouter().query.slug as string;
    const { isLoggedIn } = useUserStore();
    const { openLoginModal } = useLinkBuilder();
    const toast = useWfToast();
    const { t } = useTranslation(["news", "common", "reactions"]);
    const [userReactions, setUserReactions] = useState(getReactionsWithBarConfig(reactions, t));
    const [highestReactionValue, setHighestReactionValue] = useState<number>(getHighestReactionNumber(reactions));
    const upsertUserReactionMutation = useUpsertUserReactionMutation();

    const aggregatedReactions = useNewsUserAggregatedReactions(userReactions);
    const allReactionsCount = useMemo(() => getReactionsCount(aggregatedReactions, "numberOfReactions"), [aggregatedReactions]);

    const getUpdatedReactionObject = (reactionToUpdate: IUserReactionWithBarConfig, value: number) => {
        const updatedReaction = getObjectCopy(reactionToUpdate);
        const prevReactionValueIndex = reactionToUpdate.reactionValues.findIndex(val => val.value === reactionToUpdate.userReactionValue);
        if (prevReactionValueIndex !== -1) {
            updatedReaction.reactionValues[prevReactionValueIndex].numberOfReactions--;
        }

        const reactionValueIndex = reactionToUpdate.reactionValues.findIndex(val => val.value === value);
        updatedReaction.hasUserReacted = true;
        updatedReaction.userReactionValue = value;
        updatedReaction.reactionValues[reactionValueIndex].numberOfReactions++;

        return updatedReaction;
    };

    const getCommonRequestProperties = (reaction: IUserReactionWithBarConfig) => ({
        entityId: newsArticleId,
        entityType: EnumReactionEntityType.ExternalNewsArticle,
        reactionType: reaction.reactionType,
        nestedEntity: reaction.underlyingIsin
            ? {
                  entityId: reaction.underlyingIsin,
                  entityType: EnumReactionType.ArticleUserPricePredictionUnderlying,
              }
            : null,
    });

    const handleOnReactionChange = (index: number) => (value: number) => {
        if (!isLoggedIn) {
            return openLoginModal();
        }

        const currentReaction = userReactions[index];
        if (currentReaction.hasUserReacted && currentReaction.userReactionValue === value) {
            return;
        }

        const newReaction = getUpdatedReactionObject(currentReaction, value);
        const updatedReactions = [...userReactions];
        updatedReactions[index] = newReaction;

        upsertUserReactionMutation
            .mutatePromise({
                ...getCommonRequestProperties(newReaction),
                reactionValue: value,
            })
            .catch(() => {
                toast({ status: EnumToastStatus.Error, title: t("general-error", { ns: "common" }) });
                setUserReactions(userReactions);
                setHighestReactionValue(getHighestReactionNumber(userReactions));
            });

        setUserReactions(updatedReactions);
        setHighestReactionValue(getHighestReactionNumber(updatedReactions));
    };

    return {
        handleOnReactionChange,
        userReactions,
        highestReactionValue,
        aggregatedReactions,
        allReactionsCount,
    };
};
